/**
 * QuickLinks widget reusable — mini panel para guardar enlaces directos por sección.
 * Uso: renderQuickLinks(container, 'inmuebles') dentro de cualquier sección.
 */
import { h, apiClient, toast, openModal, emptyState } from './_common.js';

export async function renderQuickLinks(container, section, opts = {}) {
  const title = opts.title ?? '🔗 Enlaces rápidos';
  const subtitle = opts.subtitle ?? 'Tus cuentas y plataformas — un click para acceder.';

  const wrap = h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4 mb-4' });
  container.appendChild(wrap);

  const header = h('div', { class: 'flex items-center justify-between mb-3' }, [
    h('div', {}, [
      h('h3', { class: 'font-semibold text-sm' }, title),
      h('p', { class: 'text-xs text-ink-400' }, subtitle),
    ]),
    h('button', {
      class: 'bg-gold-500/20 hover:bg-gold-500/30 border border-gold-400/40 text-gold-300 text-xs px-3 py-1.5 rounded-md transition',
      onclick: () => openAddModal(section, container, opts),
    }, '+ Añadir'),
  ]);
  wrap.appendChild(header);

  const grid = h('div', { class: 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2' });
  wrap.appendChild(grid);

  try {
    const r = await apiClient.get(`/api/v1/quick-links?section=${encodeURIComponent(section)}`);
    if (!r.links || r.links.length === 0) {
      wrap.removeChild(grid);
      wrap.appendChild(emptyState('🔗', 'Sin enlaces guardados', 'Añade tus cuentas Idealista, Fotocasa, Airbnb, PriceLabs, etc. con un click directo.'));
      return;
    }
    r.links.forEach((link) => grid.appendChild(linkCard(link, section, container, opts)));
  } catch (e) {
    wrap.removeChild(grid);
    wrap.appendChild(h('div', { class: 'text-xs text-red-400' }, 'Error cargando enlaces: ' + (e?.message || e)));
  }
}

function linkCard(link, section, container, opts) {
  return h('div', { class: 'group relative bg-base-900/50 border border-base-700 rounded-lg p-3 hover:border-gold-400/40 transition' }, [
    h('a', { href: link.url, target: '_blank', rel: 'noopener noreferrer', class: 'block' }, [
      h('div', { class: 'flex items-center gap-2 mb-1' }, [
        h('span', { class: 'text-2xl' }, link.emoji || '🔗'),
        h('div', { class: 'flex-1 min-w-0' }, [
          h('div', { class: 'font-semibold text-sm truncate' }, link.label),
          link.account ? h('div', { class: 'text-xs text-ink-400 truncate' }, link.account) : null,
        ]),
      ]),
      link.notes ? h('p', { class: 'text-xs text-ink-300 line-clamp-2' }, link.notes) : null,
    ]),
    // Edit/Delete buttons on hover
    h('div', { class: 'absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition flex gap-1' }, [
      h('button', { class: 'text-xs bg-base-700 hover:bg-base-600 text-ink-200 px-1.5 py-0.5 rounded', onclick: (e) => {
        e.preventDefault(); e.stopPropagation();
        openEditModal(link, section, container, opts);
      }, title: 'Editar' }, '✏️'),
      h('button', { class: 'text-xs bg-red-500/20 hover:bg-red-500/40 text-red-300 px-1.5 py-0.5 rounded', onclick: async (e) => {
        e.preventDefault(); e.stopPropagation();
        if (!confirm(`¿Eliminar "${link.label}"?`)) return;
        try {
          await fetch(`/api/v1/quick-links/${link.id}`, { method: 'DELETE', credentials: 'include' });
          toast('Enlace eliminado', 'success');
          // Re-render
          const oldWrap = container.querySelector(`.quick-links-${section}`)?.parentElement;
          if (opts.onReload) opts.onReload();
        } catch { toast('Error eliminando', 'error'); }
      }, title: 'Eliminar' }, '🗑️'),
    ]),
  ]);
}

async function openAddModal(section, container, opts) {
  const data = await openModal({
    title: '🔗 Añadir enlace rápido',
    fields: [
      { name: 'label', label: 'Nombre (ej: Idealista cuenta principal)', required: true },
      { name: 'url', label: 'URL completa', required: true, placeholder: 'https://...' },
      { name: 'account', label: 'Email/usuario (opcional)' },
      { name: 'emoji', label: 'Emoji (opcional, se detecta automático)', placeholder: '🏠' },
      { name: 'notes', label: 'Notas (opcional)', type: 'textarea' },
    ],
    submitLabel: 'Guardar',
  });
  if (!data) return;
  try {
    await apiClient.post('/api/v1/quick-links', { ...data, section });
    toast('Enlace guardado', 'success');
    if (opts.onReload) opts.onReload();
  } catch (e) { toast('Error: ' + (e?.message || e), 'error'); }
}

async function openEditModal(link, section, container, opts) {
  const data = await openModal({
    title: '✏️ Editar enlace',
    fields: [
      { name: 'label', label: 'Nombre', defaultValue: link.label, required: true },
      { name: 'url', label: 'URL', defaultValue: link.url, required: true },
      { name: 'account', label: 'Email/usuario', defaultValue: link.account || '' },
      { name: 'emoji', label: 'Emoji', defaultValue: link.emoji || '' },
      { name: 'notes', label: 'Notas', type: 'textarea', defaultValue: link.notes || '' },
    ],
    submitLabel: 'Actualizar',
  });
  if (!data) return;
  try {
    await fetch(`/api/v1/quick-links/${link.id}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    toast('Enlace actualizado', 'success');
    if (opts.onReload) opts.onReload();
  } catch (e) { toast('Error: ' + (e?.message || e), 'error'); }
}
